import { ScrollText } from "lucide-react";
import type { PolicyData } from "../PolicyShell";

export const TERMS_OF_USE: PolicyData = {
  slug: "terms-of-use",
  title: "Terms of Use",
  subtitle: "Customer & Business Terms",
  effectiveDate: "January 2026",
  icon: ScrollText,
  documentType: "Customer & Business Terms",
  version: "1.0",
  sections: [
    {
      id: "introduction",
      number: "1",
      title: "Introduction",
      blocks: [
        { type: "p", text: "Welcome to CashTap." },
        {
          type: "p",
          text: "These Terms of Use (“Terms”) govern your access to and use of the CashTap platform, website, applications, APIs, wallets, payment links, collection services, peer-to-peer services, stablecoin-enabled services, and related financial technology services (collectively, the “Services”).",
        },
        {
          type: "p",
          text: "By accessing or using CashTap, you agree to be legally bound by these Terms.",
        },
        {
          type: "p",
          text: "If you do not agree to these Terms, you may not access or use the Services.",
        },
      ],
    },
    {
      id: "eligibility",
      number: "2",
      title: "Eligibility",
      blocks: [
        { type: "p", text: "To use CashTap, you must:" },
        {
          type: "ul",
          items: [
            "Be at least 18 years old",
            "Have legal capacity to enter into binding agreements",
            "Provide accurate and truthful information",
            "Comply with applicable laws and regulations",
            "Successfully complete required verification procedures",
          ],
        },
        {
          type: "p",
          text: "CashTap reserves the right to deny, suspend, or terminate access to any user at its discretion.",
        },
      ],
    },
    {
      id: "services-overview",
      number: "3",
      title: "Services Overview",
      blocks: [
        {
          type: "p",
          text: "CashTap provides technology and financial infrastructure services including:",
        },
        {
          type: "ul",
          items: [
            "Payment links",
            "Collection tools",
            "Wallet functionality",
            "Stablecoin-enabled services",
            "Peer-to-peer payment functionality",
            "Merchant settlement services",
            "Invoice generation",
            "API integrations",
            "Virtual account infrastructure through regulated partners",
          ],
        },
        {
          type: "p",
          text: "Certain regulated services may be provided through licensed third-party financial institutions, custodial providers, payment processors, banking partners, or compliance providers.",
        },
        {
          type: "p",
          text: "CashTap operates as a technology platform and does not directly provide banking services.",
        },
      ],
    },
    {
      id: "no-banking",
      number: "4",
      title: "No Banking Relationship",
      blocks: [
        { type: "p", text: "CashTap is not a bank." },
        { type: "p", text: "Use of the Services does not create:" },
        {
          type: "ul",
          items: [
            "A personal bank account",
            "A deposit account",
            "Direct FDIC insurance through CashTap",
            "A fiduciary relationship",
          ],
        },
        {
          type: "p",
          text: "Certain banking, custody, or payment services may be provided by regulated third-party financial institutions.",
        },
      ],
    },
    {
      id: "account-registration",
      number: "5",
      title: "Account Registration",
      blocks: [
        {
          type: "p",
          text: "Users must provide accurate, current, and complete information during registration.",
        },
        { type: "p", text: "Users agree:" },
        {
          type: "ul",
          items: [
            "Not to impersonate another person or entity",
            "Not to use false or misleading information",
            "Not to create accounts for unlawful purposes",
            "Not to bypass compliance or security controls",
            "Not to create accounts on behalf of others without authorization",
          ],
        },
        {
          type: "p",
          text: "CashTap reserves the right to request additional information at any time.",
        },
      ],
    },
    {
      id: "kyc",
      number: "6",
      title: "Identity Verification (KYC)",
      blocks: [
        {
          type: "p",
          text: "CashTap may require identity verification procedures including:",
        },
        {
          type: "ul",
          items: [
            "Government-issued identification",
            "Selfie and liveness verification",
            "Address verification",
            "Phone verification",
            "Email verification",
            "Business verification",
            "Source of funds documentation",
            "Beneficial ownership verification",
          ],
        },
        { type: "p", text: "Failure to complete verification may result in:" },
        {
          type: "ul",
          items: [
            "Restricted functionality",
            "Delayed transactions",
            "Account suspension",
            "Account termination",
          ],
        },
      ],
    },
    {
      id: "business-accounts",
      number: "7",
      title: "Business Accounts",
      blocks: [
        { type: "p", text: "Business users may be required to provide:" },
        {
          type: "ul",
          items: [
            "Corporate registration documents",
            "Tax identification numbers",
            "Beneficial ownership information",
            "Business activity descriptions",
            "Authorized representative documentation",
            "Licensing information where applicable",
          ],
        },
        {
          type: "p",
          text: "CashTap reserves the right to conduct Enhanced Due Diligence (EDD) reviews on business accounts.",
        },
      ],
    },
    {
      id: "compliance-monitoring",
      number: "8",
      title: "Compliance & Monitoring",
      blocks: [
        { type: "p", text: "CashTap may monitor:" },
        {
          type: "ul",
          items: [
            "Transactions",
            "Wallet activity",
            "Device information",
            "Login behavior",
            "API activity",
            "IP addresses",
            "Payment patterns",
            "User activity",
          ],
        },
        {
          type: "p",
          text: "CashTap may investigate suspicious activity and cooperate with regulators, banking partners, compliance vendors, and law enforcement where legally required.",
        },
      ],
    },
    {
      id: "prohibited-activities",
      number: "9",
      title: "Prohibited Activities",
      blocks: [
        { type: "p", text: "Users may NOT use CashTap for:" },
        {
          type: "ul",
          items: [
            "Money laundering",
            "Fraud",
            "Scam activity",
            "Terrorist financing",
            "Unauthorized money transmission",
            "Human trafficking",
            "Child exploitation",
            "Identity theft",
            "Synthetic identity fraud",
            "Cybercrime",
            "Sale of illegal goods",
            "Counterfeit products",
            "Sanctions evasion",
            "Darknet activity",
            "Illegal financial activity",
          ],
        },
        { type: "p", text: "Users may also NOT:" },
        {
          type: "ul",
          items: [
            "Abuse APIs or automation",
            "Circumvent platform controls",
            "Use stolen payment methods",
            "Engage in deceptive schemes",
            "Interfere with platform security",
            "Attempt unauthorized access to systems",
          ],
        },
        {
          type: "p",
          text: "Violation may result in immediate restriction, suspension, termination, and reporting to authorities.",
        },
      ],
    },
    {
      id: "restricted-businesses",
      number: "10",
      title: "Restricted Businesses",
      blocks: [
        {
          type: "p",
          text: "CashTap may prohibit or restrict businesses involving:",
        },
        {
          type: "ul",
          items: [
            "Adult content",
            "Weapons sales",
            "Illegal pharmaceuticals",
            "Counterfeit goods",
            "Pyramid schemes",
            "Anonymous financial services",
            "Fraud-prone business models",
            "Unlicensed financial services",
          ],
        },
        {
          type: "p",
          text: "Approval of any business category remains solely at CashTap’s discretion.",
        },
      ],
    },
    {
      id: "account-restrictions",
      number: "11",
      title: "Account Restrictions & Holds",
      blocks: [
        { type: "p", text: "CashTap reserves the right to:" },
        {
          type: "ul",
          items: [
            "Freeze accounts",
            "Restrict accounts",
            "Delay settlements",
            "Hold withdrawals",
            "Reject transactions",
            "Require additional verification",
            "Limit account functionality",
          ],
        },
        { type: "p", text: "Restrictions may occur due to:" },
        {
          type: "ul",
          items: [
            "Compliance reviews",
            "Fraud investigations",
            "Security concerns",
            "Regulatory obligations",
            "Chargeback exposure",
            "Suspicious activity detection",
          ],
        },
        {
          type: "p",
          text: "CashTap may maintain reserves or rolling holds where risk exposure is identified.",
        },
      ],
    },
    {
      id: "stablecoin-risks",
      number: "12",
      title: "Stablecoin & Digital Asset Risks",
      blocks: [
        {
          type: "p",
          text: "Transactions involving digital assets, stablecoins, blockchain networks, peer-to-peer transfers, wallet transfers, and payment links may involve operational, technical, compliance, fraud, and financial risks.",
        },
        { type: "p", text: "CashTap does not guarantee:" },
        {
          type: "ul",
          items: [
            "Recovery of transferred funds",
            "Successful completion of transactions",
            "Protection against scams",
            "Protection against fraudulent counterparties",
            "Blockchain network availability",
            "Third-party provider uptime",
            "Transaction profitability",
            "Compatibility with external wallets or platforms",
          ],
        },
        {
          type: "p",
          text: "Certain blockchain or stablecoin transactions may become irreversible once submitted to blockchain networks or external payment infrastructure.",
        },
        { type: "p", text: "Users are solely responsible for reviewing:" },
        {
          type: "ul",
          items: [
            "Wallet addresses",
            "Recipient information",
            "Payment details",
            "Invoice information",
            "QR codes",
            "Transaction amounts",
          ],
        },
        { type: "p", text: "CashTap shall not be responsible for losses caused by:" },
        {
          type: "ul",
          items: [
            "Incorrect wallet addresses",
            "Fraudulent counterparties",
            "Scams",
            "Phishing attacks",
            "Social engineering",
            "Fake investment opportunities",
            "Malware",
            "Device compromise",
            "Unauthorized third-party access",
            "Blockchain failures",
            "Third-party provider interruptions",
          ],
        },
        { type: "h3", text: "Irreversible Transactions & No Refund Policy" },
        {
          type: "p",
          text: "Users acknowledge and agree that transactions processed through CashTap, including but not limited to stablecoin transfers, blockchain transactions, wallet transfers, peer-to-peer payments, payment links, and digital asset transactions, may become irreversible once submitted or completed.",
        },
        {
          type: "p",
          text: "Once a transaction has been processed, confirmed, settled, or transmitted through blockchain networks, payment infrastructure, custodial providers, banking partners, or third-party service providers, CashTap may not be able to reverse, cancel, recover, or refund the transaction.",
        },
        {
          type: "p",
          text: "CashTap does not guarantee refunds, reversals, chargeback rights, or recovery of funds for:",
        },
        {
          type: "ul",
          items: [
            "User mistakes",
            "Incorrect wallet addresses",
            "Incorrect recipient information",
            "Fraudulent counterparties",
            "Scam transactions",
            "Unauthorized third-party conduct",
            "Blockchain-related errors",
            "User negligence",
            "Digital asset market losses",
          ],
        },
        {
          type: "p",
          text: "Users are solely responsible for verifying all transaction details before authorizing or submitting transactions.",
        },
        {
          type: "p",
          text: "By using CashTap, users acknowledge and agree that completed transactions may be final and non-refundable.",
        },
      ],
    },
    {
      id: "fraud-awareness",
      number: "13",
      title: "Fraud & Scam Awareness Notice",
      blocks: [
        {
          type: "p",
          text: "Users must exercise caution before sending payments or digital assets to third parties.",
        },
        {
          type: "p",
          text: "CashTap strongly advises users to conduct their own due diligence before entering into any transaction, including transactions involving:",
        },
        {
          type: "ul",
          items: [
            "Peer-to-peer payments",
            "Online sellers",
            "Merchants",
            "Investment opportunities",
            "Marketplace transactions",
            "Social media offers",
            "QR code payments",
            "Wallet transfers",
          ],
        },
        {
          type: "p",
          text: "Users acknowledge that scammers may impersonate legitimate businesses, merchants, or individuals.",
        },
        {
          type: "p",
          text: "CashTap does not verify, endorse, or guarantee third-party products, services, investment opportunities, or counterparties unless explicitly stated otherwise.",
        },
        {
          type: "p",
          text: "Users are solely responsible for evaluating the legitimacy of transactions and counterparties before transferring funds.",
        },
        {
          type: "p",
          text: "CashTap shall not be responsible or liable for losses arising from:",
        },
        {
          type: "ul",
          items: [
            "Fraudulent transactions",
            "Scam activity",
            "Fake investment opportunities",
            "Social engineering attacks",
            "Phishing attempts",
            "Counterfeit merchants",
            "Unauthorized third-party conduct",
            "Misrepresentation by counterparties",
          ],
        },
        {
          type: "p",
          text: "Users acknowledge and agree that all transaction decisions are made entirely at their own risk and responsibility.",
        },
      ],
    },
    {
      id: "payment-processing",
      number: "14",
      title: "Payment Processing & Settlement",
      blocks: [
        { type: "p", text: "Settlement times may vary based on:" },
        {
          type: "ul",
          items: [
            "Payment method",
            "Banking schedules",
            "Compliance reviews",
            "Blockchain confirmation times",
            "Fraud monitoring",
            "Risk assessments",
            "Third-party provider availability",
          ],
        },
        {
          type: "p",
          text: "CashTap does not guarantee instant settlement availability.",
        },
      ],
    },
    {
      id: "fees",
      number: "15",
      title: "Fees",
      blocks: [
        { type: "p", text: "CashTap may charge fees including:" },
        {
          type: "ul",
          items: [
            "Processing fees",
            "Settlement fees",
            "Withdrawal fees",
            "Conversion fees",
            "API usage fees",
            "Merchant service fees",
          ],
        },
        {
          type: "p",
          text: "Applicable fees may be disclosed through the platform, APIs, merchant dashboards, invoices, or separate agreements.",
        },
      ],
    },
    {
      id: "chargebacks",
      number: "16",
      title: "Chargebacks & Disputes",
      blocks: [
        { type: "p", text: "Businesses and users remain responsible for:" },
        {
          type: "ul",
          items: [
            "Valid transaction activity",
            "Fraud prevention",
            "Customer communication",
            "Supporting transaction legitimacy",
          ],
        },
        { type: "p", text: "CashTap may:" },
        {
          type: "ul",
          items: [
            "Hold reserves",
            "Delay settlements",
            "Restrict accounts",
            "Recover losses associated with disputes, fraud, or chargebacks",
          ],
        },
        {
          type: "p",
          text: "Businesses remain responsible for excessive dispute activity or fraudulent conduct associated with their accounts.",
        },
      ],
    },
    {
      id: "merchant-responsibilities",
      number: "17",
      title: "Merchant Responsibilities",
      blocks: [
        { type: "p", text: "Business users agree to:" },
        {
          type: "ul",
          items: [
            "Operate legally",
            "Maintain required licenses",
            "Deliver promised goods and services",
            "Avoid deceptive practices",
            "Maintain accurate business information",
            "Cooperate with compliance requests",
          ],
        },
        {
          type: "p",
          text: "Businesses remain solely responsible for interactions with their customers.",
        },
      ],
    },
    {
      id: "api-usage",
      number: "18",
      title: "API Usage",
      blocks: [
        { type: "p", text: "Developers and API users agree:" },
        {
          type: "ul",
          items: [
            "Not to abuse APIs",
            "Not to bypass rate limits",
            "Not to automate fraudulent activity",
            "Not to compromise platform security",
            "To maintain secure integrations",
          ],
        },
        { type: "p", text: "CashTap may suspend or revoke API access at any time." },
      ],
    },
    {
      id: "user-responsibility",
      number: "19",
      title: "User Responsibility",
      blocks: [
        { type: "p", text: "Users are solely responsible for:" },
        {
          type: "ul",
          items: [
            "Verifying recipient information",
            "Conducting due diligence before making transactions",
            "Evaluating transaction legitimacy",
            "Protecting account credentials",
            "Maintaining device security",
            "Monitoring account activity",
            "Avoiding suspicious or fraudulent activity",
          ],
        },
        {
          type: "p",
          text: "Users acknowledge that they use CashTap Services entirely at their own risk.",
        },
      ],
    },
    {
      id: "third-party-providers",
      number: "20",
      title: "Third-Party Providers",
      blocks: [
        { type: "p", text: "CashTap may rely on third-party providers including:" },
        {
          type: "ul",
          items: [
            "Banking partners",
            "Custodial providers",
            "Payment processors",
            "Compliance vendors",
            "Blockchain infrastructure providers",
            "Wallet infrastructure providers",
          ],
        },
        {
          type: "p",
          text: "CashTap is not responsible for interruptions, failures, delays, or actions caused by third-party providers.",
        },
      ],
    },
    {
      id: "counterparty-disclaimer",
      number: "21",
      title: "Third-Party Counterparty Disclaimer",
      blocks: [
        {
          type: "p",
          text: "CashTap acts solely as a technology and financial infrastructure platform.",
        },
        {
          type: "p",
          text: "CashTap does not guarantee the legitimacy, identity, licensing, solvency, reputation, conduct, or performance of third-party users, merchants, businesses, customers, or counterparties using the platform.",
        },
        {
          type: "p",
          text: "Users interact with third parties entirely at their own risk.",
        },
      ],
    },
    {
      id: "security",
      number: "22",
      title: "Security",
      blocks: [
        { type: "p", text: "Users are responsible for:" },
        {
          type: "ul",
          items: [
            "Protecting credentials",
            "Maintaining device security",
            "Enabling available security features",
            "Monitoring account activity",
          ],
        },
        {
          type: "p",
          text: "CashTap is not responsible for losses caused by compromised credentials, phishing attacks, malware, social engineering, or user negligence.",
        },
      ],
    },
    {
      id: "sanctions",
      number: "23",
      title: "Sanctions Compliance",
      blocks: [
        { type: "p", text: "Users may not use CashTap if they are:" },
        {
          type: "ul",
          items: [
            "Located in sanctioned jurisdictions",
            "Subject to sanctions restrictions",
            "Acting on behalf of sanctioned parties",
          ],
        },
        {
          type: "p",
          text: "CashTap reserves the right to restrict or terminate accounts for sanctions compliance purposes.",
        },
      ],
    },
    {
      id: "suspension-termination",
      number: "24",
      title: "Suspension & Termination",
      blocks: [
        {
          type: "p",
          text: "CashTap may suspend or terminate access at any time for reasons including:",
        },
        {
          type: "ul",
          items: [
            "Violations of these Terms",
            "Fraud risk",
            "Compliance concerns",
            "Regulatory obligations",
            "Security threats",
            "Suspicious activity",
          ],
        },
        {
          type: "p",
          text: "Termination may occur without prior notice where legally permitted.",
        },
      ],
    },
    {
      id: "intellectual-property",
      number: "25",
      title: "Intellectual Property",
      blocks: [
        {
          type: "p",
          text: "All platform content, software, trademarks, logos, APIs, interfaces, designs, and branding remain the property of CashTap or its licensors.",
        },
        { type: "p", text: "Users may not:" },
        {
          type: "ul",
          items: [
            "Copy platform code",
            "Reverse engineer systems",
            "Reproduce branding",
            "Use platform assets without authorization",
          ],
        },
      ],
    },
    {
      id: "privacy",
      number: "26",
      title: "Privacy",
      blocks: [
        {
          type: "p",
          text: "Use of CashTap is also governed by the CashTap Privacy Policy.",
        },
        { type: "p", text: "CashTap may collect and process:" },
        {
          type: "ul",
          items: [
            "Identity information",
            "Transaction information",
            "Device information",
            "Wallet addresses",
            "Compliance information",
            "Usage analytics",
          ],
        },
        {
          type: "p",
          text: "Information may be shared with third-party providers where necessary to provide Services or comply with legal obligations.",
        },
      ],
    },
    {
      id: "no-warranty",
      number: "27",
      title: "No Warranty",
      blocks: [
        {
          type: "p",
          text: "CashTap Services are provided “as is” and “as available.”",
        },
        { type: "p", text: "CashTap disclaims all warranties including:" },
        {
          type: "ul",
          items: [
            "Merchantability",
            "Fitness for a particular purpose",
            "Availability",
            "Non-infringement",
            "Error-free operation",
          ],
        },
        { type: "p", text: "CashTap does not guarantee uninterrupted platform availability." },
      ],
    },
    {
      id: "no-liability",
      number: "28",
      title: "No Liability for User Losses",
      blocks: [
        {
          type: "p",
          text: "To the maximum extent permitted by law, CashTap, Aixona LLC, affiliates, officers, employees, contractors, service providers, banking partners, custodial providers, and infrastructure providers shall not be liable for:",
        },
        {
          type: "ul",
          items: [
            "Financial losses",
            "Fraud losses",
            "Scam-related losses",
            "Stablecoin losses",
            "Blockchain-related losses",
            "Unauthorized transfers",
            "Incorrect wallet transfers",
            "Chargebacks",
            "Market losses",
            "Lost profits",
            "Lost business opportunities",
            "Delayed transactions",
            "Failed transactions",
            "Third-party misconduct",
            "User mistakes",
            "System interruptions",
          ],
        },
        {
          type: "p",
          text: "Users acknowledge and agree that they assume full responsibility for transactions initiated through the platform.",
        },
      ],
    },
    {
      id: "high-risk-warning",
      number: "29",
      title: "High-Risk Transaction Warning",
      blocks: [
        {
          type: "p",
          text: "Transactions involving digital assets, stablecoins, peer-to-peer transfers, payment links, and online counterparties may involve elevated fraud and financial crime risks.",
        },
        { type: "p", text: "Users should never send funds to:" },
        {
          type: "ul",
          items: [
            "Unknown persons",
            "Unverified sellers",
            "Individuals promising guaranteed returns",
            "Suspicious investment opportunities",
            "Parties requesting secrecy",
            "Parties requesting urgent payments",
          ],
        },
        {
          type: "p",
          text: "CashTap may investigate suspicious activity but does not guarantee prevention of all fraudulent activity.",
        },
      ],
    },
    {
      id: "indemnification",
      number: "30",
      title: "Indemnification",
      blocks: [
        {
          type: "p",
          text: "Users agree to indemnify and hold harmless CashTap, Aixona LLC, affiliates, employees, contractors, partners, and service providers from claims arising from:",
        },
        {
          type: "ul",
          items: [
            "User misconduct",
            "Violations of law",
            "Fraudulent activity",
            "Breach of these Terms",
            "Disputes between businesses and customers",
          ],
        },
      ],
    },
    {
      id: "regulatory-cooperation",
      number: "31",
      title: "Regulatory Cooperation",
      blocks: [
        { type: "p", text: "CashTap may cooperate with:" },
        {
          type: "ul",
          items: [
            "Law enforcement agencies",
            "Regulatory authorities",
            "Courts",
            "Financial institutions",
            "Banking partners",
            "Compliance providers",
          ],
        },
        { type: "p", text: "CashTap may disclose information where legally required." },
      ],
    },
    {
      id: "modifications",
      number: "32",
      title: "Modifications to Services",
      blocks: [
        { type: "p", text: "CashTap may:" },
        {
          type: "ul",
          items: [
            "Modify Services",
            "Suspend features",
            "Add restrictions",
            "Remove functionality",
            "Introduce new Services",
          ],
        },
        { type: "p", text: "At any time without prior notice." },
      ],
    },
    {
      id: "amendments",
      number: "33",
      title: "Amendments to Terms",
      blocks: [
        { type: "p", text: "CashTap may update these Terms periodically." },
        {
          type: "p",
          text: "Continued use of the Services after updates constitutes acceptance of revised Terms.",
        },
      ],
    },
    {
      id: "governing-law",
      number: "34",
      title: "Governing Law",
      blocks: [
        {
          type: "p",
          text: "These Terms shall be governed by applicable laws of the United States and relevant state laws without regard to conflict of law principles.",
        },
      ],
    },
    {
      id: "dispute-resolution",
      number: "35",
      title: "Dispute Resolution",
      blocks: [
        { type: "p", text: "Disputes may be resolved through:" },
        {
          type: "ul",
          items: [
            "Arbitration",
            "Courts of competent jurisdiction",
            "Informal resolution procedures",
          ],
        },
        { type: "p", text: "Where permitted under applicable law." },
      ],
    },
    {
      id: "severability",
      number: "36",
      title: "Severability",
      blocks: [
        {
          type: "p",
          text: "If any provision of these Terms is determined unenforceable, the remaining provisions shall remain in full force and effect.",
        },
      ],
    },
    {
      id: "entire-agreement",
      number: "37",
      title: "Entire Agreement",
      blocks: [
        {
          type: "p",
          text: "These Terms constitute the complete agreement between users and CashTap regarding the Services.",
        },
      ],
    },
    {
      id: "contact",
      number: "38",
      title: "Contact Information",
      blocks: [
        { type: "p", text: "For legal, compliance, or support inquiries:" },
        { type: "h3", text: "CashTap Compliance Team" },
        { type: "p", text: "" },
        {
          type: "p",
          text: "Official communication channels may be provided through the CashTap platform.",
        },
      ],
    },
    {
      id: "final-acknowledgment",
      number: "39",
      title: "Final Legal Acknowledgment",
      blocks: [
        { type: "p", text: "By using CashTap, users acknowledge and agree that:" },
        {
          type: "ul",
          items: [
            "They conduct transactions at their own risk",
            "They are responsible for performing their own due diligence",
            "Certain digital asset or blockchain transactions may become irreversible",
            "Completed transactions may be final and non-refundable",
            "CashTap is not liable for user losses or third-party fraud",
            "CashTap may monitor, investigate, restrict, or report suspicious activity",
            "Users remain solely responsible for their financial decisions and transaction activity",
          ],
        },
      ],
    },
  ],
  footerNote: "Version 1.0 — January 2026. Initial Customer & Business Terms of Use.",
};
